import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { get } from 'lodash';
import React, { FC } from 'react';
import theme from 'src/assets/theme/theme';
import { useApplication } from 'src/context/ApplicationContext';
import { KOSTEN_ARTEN, KOSTEN_POSITION } from 'src/lib/constants/constant';
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenpositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import { DisplayEuro } from 'src/utils/formats';

const P = AntragSchema.attributes;
const KP_P = KostenpositionSchema.attributes;
const LABEL_COLUMN_WIDTH = '42%';

const FormantragFinanzierungsTabelle: FC = () => {
	const { application } = useApplication();
	const allKP: any[] = get(application, P.Foerderantrag_Kostenpositions.path, []);

	const ausgabeKP = allKP.filter((kp) => get(kp, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe);
	const eigenmittelKP = allKP.filter(
		(kp) =>
			get(kp, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme &&
			get(kp, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel,
	);

	const years: number[] = Array.from(
		new Set(allKP.flatMap((kp) => get(kp, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).map((k: any) => Number(k.Zelle_1)))),
	)
		.filter((y) => !isNaN(y))
		.sort((a, b) => a - b);

	const getYearlySum = (kps: any[], year: number): number =>
		kps.reduce((total, kostenPosition) => {
			const yearEntry = get(kostenPosition, KP_P.Allgemein_Kosten_Pro_Jahr.path, []).find((k: any) => Number(k.Zelle_1) === year);
			return total + (parseFloat(get(yearEntry, 'Zelle_2', '0')) || 0);
		}, 0);

	const getKategorieRows = () => {
		const groupedByKategorie = eigenmittelKP.reduce((result: Record<string, any[]>, kp) => {
			const kategorie = get(kp, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Bezeichnung.path, '');
			if (!result[kategorie]) {
				result[kategorie] = [];
			}
			result[kategorie].push(kp);
			return result;
		}, {});

		return Object.entries(groupedByKategorie).map(([kategorie, kategorieKP]) => {
			const yearlyValues = years.map((year) => getYearlySum(kategorieKP, year));
			const total = yearlyValues.reduce((sum, value) => sum + value, 0);
			return { kategorie, yearlyValues, total };
		});
	};

	const kategorieRows = getKategorieRows();
	const ausgabenPerYear = years.map((year) => getYearlySum(ausgabeKP, year));
	const eigenmittelPerYear = years.map((year) => getYearlySum(eigenmittelKP, year));
	const zuwendungPerYear = years.map((_, index) => ausgabenPerYear[index] - eigenmittelPerYear[index]);

	const zuwendungTotal = zuwendungPerYear.reduce((sum, value) => sum + value, 0);
	const gesamtFinanzierungPerYear = years.map((_, index) => eigenmittelPerYear[index] + zuwendungPerYear[index]);
	const gesamtFinanzierungTotal = gesamtFinanzierungPerYear.reduce((sum, value) => sum + value, 0);

	return (
		<TableContainer>
			<Table size='medium' aria-label='a dense table' sx={{ tableLayout: 'fixed', width: '100%' }}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ backgroundColor: theme.palette.background.paper, width: LABEL_COLUMN_WIDTH }}>Euro</TableCell>
						{years.map((y) => (
							<TableCell key={y} align='right' sx={{ backgroundColor: theme.palette.background.paper }}>
								{y}
							</TableCell>
						))}
						<TableCell align='right' sx={{ backgroundColor: theme.palette.background.paper }}>
							Gesamt
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{kategorieRows.map((row) => (
						<TableRow key={row.kategorie}>
							<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>{row.kategorie}</TableCell>
							{row.yearlyValues.map((value, index) => (
								<TableCell key={index} align='right'>
									{DisplayEuro(parseFloat(value.toFixed(2)))}
								</TableCell>
							))}
							<TableCell align='right'>{DisplayEuro(row.total)}</TableCell>
						</TableRow>
					))}
					<TableRow>
						<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>Zuwendung</TableCell>
						{zuwendungPerYear.map((value, index) => (
							<TableCell key={index} align='right'>
								{DisplayEuro(parseFloat(value.toFixed(2)))}
							</TableCell>
						))}
						<TableCell align='right'>{DisplayEuro(zuwendungTotal)}</TableCell>
					</TableRow>
					<TableRow className='total-row'>
						<TableCell sx={{ width: LABEL_COLUMN_WIDTH }}>Gesamtfinanzierung</TableCell>
						{gesamtFinanzierungPerYear.map((value, index) => (
							<TableCell key={index} align='right'>
								{DisplayEuro(parseFloat(value.toFixed(2)))}
							</TableCell>
						))}
						<TableCell align='right'>{DisplayEuro(gesamtFinanzierungTotal)}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

export default FormantragFinanzierungsTabelle;
